import AgGrid from "../../../common/AgGrid";
import { useRef, useEffect, useState, useMemo } from "react";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SuccessMessage from '../../../common/SuccessMessage';
import { useNavigate } from 'react-router-dom';
// ✅ RBAC
import { getUserTypeID } from '../../../../utils/PermissionUtils';

// ---------------------------------------------------------------------------
// SHARED FORMATTERS
// ---------------------------------------------------------------------------
const dateFormatter = (params) => {
    if (!params.value) return "";
    const d = new Date(params.value);
    if (isNaN(d.getTime())) return "";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
};

const numberFormatter = (params) =>
    params.value != null && params.value !== ""
        ? Number(params.value).toLocaleString('en-US')
        : "";

const currencyFormatter = (params) => {
    if (params.value == null || params.value === "") return "";
    const num = Number(params.value);
    if (isNaN(num)) return "";
    return num < 0
        ? `-$${Math.abs(num).toLocaleString('en-US')}`
        : `$${num.toLocaleString('en-US')}`;
};

const perPieceFormatter = (params) => {
    if (params.value == null) return "";
    const num = Number(params.value);
    if (isNaN(num)) return params.value;
    return num < 0 ? `-$${Math.abs(num)}` : `$${num}`;
};
// ---------------------------------------------------------------------------
// SHARED CELL STYLE
// ---------------------------------------------------------------------------

const baseCellStyle = (params) => {
    const isTotal = params.data?.isTotalsRow;

    return {
        fontWeight: isTotal ? 'bold' : 'normal',
        backgroundColor: isTotal ? '#d3eaff' : undefined,
        textAlign: 'right',
        paddingRight: '15px'
    };
};


const jobIdCellRenderer = (params) => {
    const isTotal = params.data?.isTotalsRow;

    if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
    }

    return (
        <span style={{ color: "#2563eb", cursor: "pointer", textDecoration: "underline" }}>
            {params.value}
        </span>
    );
};
// ---------------------------------------------------------------------------
// COLUMN DEFS — JOB DETAIL
// ---------------------------------------------------------------------------

const TooltipText = ({ text, maxLength = 25, customDisplay = null }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const textRef = useRef(null);

    const needsTruncation = customDisplay
        ? customDisplay !== text
        : (text && text.length > maxLength);

    const displayText = customDisplay
        ? customDisplay
        : (needsTruncation ? `${text.substring(0, maxLength)}...` : text);

    const handleMouseEnter = (e) => {
        if (needsTruncation) {
            const rect = e.target.getBoundingClientRect();
            setTooltipPosition({
                top: rect.top - 35,
                left: rect.left + (rect.width / 2)
            });
            setShowTooltip(true);
        }
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    return (
        <div
            ref={textRef}
            style={{ position: 'relative', display: 'inline-block', width: '100%' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <span style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: needsTruncation ? 'help' : 'default'
            }}>
                {displayText}
            </span>
            {showTooltip && needsTruncation && (
                <div
                    style={{
                        position: 'fixed',
                        top: tooltipPosition.top,
                        left: tooltipPosition.left,
                        transform: 'translateX(-50%)',
                        backgroundColor: '#333',
                        color: '#fff',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        maxWidth: '300px',
                        wordWrap: 'break-word',
                        zIndex: 10000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        whiteSpace: 'normal',
                        pointerEvents: 'none'
                    }}
                >
                    {text}
                    <div
                        style={{
                            position: 'absolute',
                            bottom: '-6px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent',
                            borderTop: '6px solid #333'
                        }}
                    />
                </div>
            )}
        </div>
    );
};

const CustomTooltip = (props) => {
    const { value } = props;

    if (!value) return null;

    return (
        <div
            style={{
                backgroundColor: '#333',
                color: '#fff',
                padding: '8px 10px',
                borderRadius: '4px',
                fontSize: '12px',
                maxWidth: '300px',
                whiteSpace: 'normal',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}
        >
            {value}
        </div>
    );
};

const jobDetailColumnDefs = [
    {
        headerName: "Client ID", field: "clientID", minWidth: 230,
        filter: 'agTextColumnFilter',
        cellStyle: baseCellStyle,

    },
    {
        headerName: "Client Name", field: "clientName", minWidth: 340, filter: 'agTextColumnFilter',
        tooltipField: 'clientName',
        tooltipComponent: 'customTooltip',
        cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
            }

            return <TooltipText text={params.value} maxLength={40} />;
        },
    },
    {
        headerName: "Job ID",
        field: "jobID",
        minWidth: 200,
        filter: 'agTextColumnFilter',
        cellRenderer: jobIdCellRenderer,
        cellStyle: baseCellStyle
    },
    {
        headerName: "Job Name",
        field: "jobName",
        minWidth: 440,
        filter: 'agTextColumnFilter',
        cellStyle: baseCellStyle,
        tooltipField: 'jobName',
        tooltipComponent: 'customTooltip',
        cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
            }

            return <TooltipText text={params.value} maxLength={35} />;
        }
    },
    {
        headerName: "Job Type Code", field: "jobTypeCode", minWidth: 280, filter: 'agTextColumnFilter',
        cellStyle: baseCellStyle
    },
    {
        headerName: "Job Type", field: "jobType", minWidth: 280, filter: 'agTextColumnFilter',
        cellStyle: baseCellStyle
    },
    {
        headerName: "Mail Code", field: "mailCode", minWidth: 280, filter: 'agTextColumnFilter',
        cellStyle: baseCellStyle
    },
    {
        headerName: "Test", field: "test", minWidth: 280, filter: 'agTextColumnFilter',
        cellStyle: baseCellStyle
    },
    {
        headerName: "Postage", field: "postage", minWidth: 280, filter: 'agTextColumnFilter',
        cellStyle: baseCellStyle
    },
    {
        headerName: "List Name", field: "listName", minWidth: 380, filter: 'agTextColumnFilter',
        tooltipField: 'listName',
        tooltipComponent: 'customTooltip',
        cellStyle: baseCellStyle
    },
    {
        headerName: "List Select",
        field: "listSelect",
        tooltipField: 'listSelect',
        tooltipComponent: 'customTooltip',
        minWidth: 500,
        filter: 'agTextColumnFilter',
        cellStyle: baseCellStyle,
    },
    {
        headerName: "Mail Date",
        field: "mailDate",
        filter: 'agTextColumnFilter',
          comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
        minWidth: 280,
        valueFormatter: dateFormatter,
        cellStyle: baseCellStyle
    },
    {
        headerName: "Job Date",
        field: "jobDate",
        filter: 'agTextColumnFilter',
          comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
        minWidth: 280,
        valueFormatter: dateFormatter,
        cellStyle: baseCellStyle
    },
    {
        headerName: "Returns Through",
        field: "returnsThrough",
        filter: 'agTextColumnFilter',
        minWidth: 280,
        valueFormatter: dateFormatter,
        cellStyle: baseCellStyle
    },
    {
        headerName: "Last Updated",
        field: "lastUpdated",
        filter: 'agTextColumnFilter',
          comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
        minWidth: 280,
        valueFormatter: dateFormatter,
        cellStyle: baseCellStyle
    },
    {
        headerName: "Last Updated By", field: "lastUpdatedBy", minWidth: 280, filter: 'agTextColumnFilter',
        cellStyle: baseCellStyle
    },
    {
        headerName: "Order Quantity", field: "orderQuantity", minWidth: 280, filter: 'agTextColumnFilter',
        valueFormatter: numberFormatter,
        cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;
            const formattedValue = params.valueFormatted ?? params.value;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
            }

            return <TooltipText text={params.valueFormatted ?? params.value} maxLength={30} />;
        }
    },
    {
        headerName: "Quantity Mailed", field: "quantityMailed", minWidth: 280, filter: 'agTextColumnFilter',
        valueFormatter: numberFormatter,
        cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;

            const formattedValue = params.valueFormatted ?? params.value;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
            }

            return <TooltipText text={params.valueFormatted ?? params.value} maxLength={30} />;
        }
    },
    {
        headerName: "Total Job Mailed Volume", field: "totalJobMailedVolume", minWidth: 280, filter: 'agTextColumnFilter',
        valueFormatter: numberFormatter,
        cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;

            const formattedValue = params.valueFormatted ?? params.value;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
            }

            return <TooltipText text={params.valueFormatted ?? params.value} maxLength={30} />;
        }
    },
    {
        headerName: "Total Job Returns", field: "totalJobReturns", minWidth: 280, filter: 'agTextColumnFilter',
        valueFormatter: numberFormatter,
        cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;

            const formattedValue = params.valueFormatted ?? params.value;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
            }

            return <TooltipText text={params.valueFormatted ?? params.value} maxLength={30} />;
        }
    },
    {
        headerName: "Non Donors", field: "nonDonors", minWidth: 280, filter: 'agTextColumnFilter',
        valueFormatter: numberFormatter,
        cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;

            const formattedValue = params.valueFormatted ?? params.value;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
            }

            return <TooltipText text={params.valueFormatted ?? params.value} maxLength={30} />;
        }
    },
    {
        headerName: "Donors", field: "donors", minWidth: 280, filter: 'agTextColumnFilter',
        valueFormatter: numberFormatter,
        cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;

            const formattedValue = params.valueFormatted ?? params.value;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
            }

            return <TooltipText text={params.valueFormatted ?? params.value} maxLength={30} />;
        }
    },
    {
        headerName: "Income",
        field: "income",
        filter: 'agTextColumnFilter',
        minWidth: 250,
        valueFormatter: currencyFormatter,
        cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;

            const formattedValue = params.valueFormatted ?? params.value;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
            }

            return <TooltipText text={params.valueFormatted ?? params.value} maxLength={30} />;
        }
    },
    {
        headerName: "Cost",
        field: "cost",
        filter: 'agTextColumnFilter',
        minWidth: 250,
        valueFormatter: currencyFormatter,
        cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;

            const formattedValue = params.valueFormatted ?? params.value;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
            }

            return <TooltipText text={params.valueFormatted ?? params.value} maxLength={30} />;
        }
    },
    {
        headerName: "Net Income",
        field: "netIncome",
        filter: 'agTextColumnFilter',
        minWidth: 250,
        valueFormatter: currencyFormatter,
        cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;

            const formattedValue = params.valueFormatted ?? params.value;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
            }

            return <TooltipText text={params.valueFormatted ?? params.value} maxLength={30} />;
        }
    },
    {
        headerName: "Response Rate",
        field: "responseRate",
        filter: 'agTextColumnFilter',
        minWidth: 280,
        valueFormatter: (params) => (params.value != null ? `${params.value}%` : ""),
        cellStyle: baseCellStyle,
        cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;

            const formattedValue = params.valueFormatted ?? params.value;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
            }

            return <TooltipText text={params.valueFormatted ?? params.value} maxLength={30} />;
        }
    },
    {
        headerName: "Average Gift",
        filter: 'agTextColumnFilter',
        field: "averageGift",
        minWidth: 280,
        valueFormatter: currencyFormatter,
        cellStyle: baseCellStyle,
        cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;

            const formattedValue = params.valueFormatted ?? params.value;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
            }

            return <TooltipText text={params.valueFormatted ?? params.value} maxLength={30} />;
        }
    },
    {
        headerName: "Income / Piece",
        field: "incomePerPiece",
        minWidth: 280,
        filter: 'agTextColumnFilter',
        valueFormatter: perPieceFormatter,
        cellStyle: baseCellStyle,
        cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;

            const formattedValue = params.valueFormatted ?? params.value;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
            }

            return <TooltipText text={params.valueFormatted ?? params.value} maxLength={30} />;
        }
    },
    {
        headerName: "Cost / Piece",
        field: "costPerPiece",
        minWidth: 250,
        filter: 'agTextColumnFilter',
        valueFormatter: perPieceFormatter,
        cellStyle: baseCellStyle,
        cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;

            const formattedValue = params.valueFormatted ?? params.value;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
            }

            return <TooltipText text={params.valueFormatted ?? params.value} maxLength={30} />;
        }
    },
    {
        headerName: "Net / Piece",
        field: "netPerPiece",
        filter: 'agTextColumnFilter',
        minWidth: 280,
        valueFormatter: perPieceFormatter,
        cellStyle: baseCellStyle,
        cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;

            const formattedValue = params.valueFormatted ?? params.value;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
            }

            return <TooltipText text={params.valueFormatted ?? params.value} maxLength={30} />;
        }
    },
    {
        headerName: "ROI", field: "incomeToOutlayRatio", minWidth: 280, filter: 'agTextColumnFilter',
        cellStyle: baseCellStyle, cellRenderer: (params) => {
            const isTotal = params.data?.isTotalsRow;

            const formattedValue = params.valueFormatted ?? params.value;

            if (isTotal) {
                return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
            }

            return <TooltipText text={params.valueFormatted ?? params.value} maxLength={30} />;
        }
    },
    {
        headerName: "First Return Date",
        field: "firstReturnDate",
        minWidth: 280,
          comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
        filter: 'agTextColumnFilter',
        valueFormatter: dateFormatter,
        cellStyle: baseCellStyle
    },
];
// ---------------------------------------------------------------------------
// COLUMN DEFS — JOB DAILY
// ---------------------------------------------------------------------------
const jobDailyColumnDefs = [
    { headerName: "Client ID", field: "clientID", minWidth: 230, filter: 'agTextColumnFilter', cellStyle: baseCellStyle },
    {
        headerName: "Client Name", field: "clientName", minWidth: 340, filter: 'agTextColumnFilter', tooltipField: 'clientName',
        tooltipComponent: 'customTooltip', cellStyle: baseCellStyle
    },
    { headerName: "Job ID", field: "jobID", minWidth: 280, filter: 'agTextColumnFilter', cellRenderer: jobIdCellRenderer, cellStyle: baseCellStyle },
    {
        headerName: "Job Name", field: "jobName", minWidth: 380, filter: 'agTextColumnFilter', tooltipField: 'jobName',
        tooltipComponent: 'customTooltip', cellStyle: baseCellStyle
    },
    { headerName: "Mail Date", field: "mailDate", filter: 'agTextColumnFilter',   comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB), minWidth: 280, valueFormatter: dateFormatter, cellStyle: baseCellStyle },
    { headerName: "Quantity Mailed", field: "quantityMailed", minWidth: 280, filter: 'agTextColumnFilter', valueFormatter: numberFormatter, cellStyle: baseCellStyle },
    { headerName: "Last Drop Date", field: "lastDropDate",   comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB), filter: 'agTextColumnFilter', minWidth: 280, valueFormatter: dateFormatter, cellStyle: baseCellStyle },
    { headerName: "Date", field: "date", filter: 'agTextColumnFilter',   comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB), minWidth: 280, valueFormatter: dateFormatter, cellStyle: baseCellStyle },
    { headerName: "Non Donors (Day)", field: "nonDonorsDay", minWidth: 280, filter: 'agTextColumnFilter', valueFormatter: numberFormatter, cellStyle: baseCellStyle },
    { headerName: "Donors (Day)", field: "donorsDay", minWidth: 280, filter: 'agTextColumnFilter', valueFormatter: numberFormatter, cellStyle: baseCellStyle },
    { headerName: "Amount (Day)", field: "amountDay", minWidth: 280, filter: 'agTextColumnFilter', valueFormatter: currencyFormatter, cellStyle: baseCellStyle },
    { headerName: "Non Donors Cumulative", field: "nonDonorsCumulative", minWidth: 280, filter: 'agTextColumnFilter', valueFormatter: numberFormatter, cellStyle: baseCellStyle },
    { headerName: "Donors Cumulative", field: "donorsCumulative", minWidth: 280, filter: 'agTextColumnFilter', valueFormatter: numberFormatter, cellStyle: baseCellStyle },
    { headerName: "Amount Cumulative", field: "amountCumulative", minWidth: 280, filter: 'agTextColumnFilter', valueFormatter: currencyFormatter, cellStyle: baseCellStyle },
    { headerName: "Total Income", field: "totalIncome", filter: 'agTextColumnFilter', minWidth: 280, valueFormatter: currencyFormatter, cellStyle: baseCellStyle },
    { headerName: "Total Non Donors", field: "totalNonDonors", filter: 'agTextColumnFilter', minWidth: 280, valueFormatter: numberFormatter, cellStyle: baseCellStyle },
    { headerName: "Total Donors", field: "totalDonors", filter: 'agTextColumnFilter', minWidth: 280, valueFormatter: numberFormatter, cellStyle: baseCellStyle },
    { headerName: "Total Cost", field: "totalCost", filter: 'agTextColumnFilter', minWidth: 280, valueFormatter: currencyFormatter, cellStyle: baseCellStyle },
    { headerName: "Production Cost", field: "productionCosts", filter: 'agTextColumnFilter', minWidth: 280, valueFormatter: currencyFormatter, cellStyle: baseCellStyle },
    { headerName: "Days Out", field: "daysOut", filter: 'agTextColumnFilter', minWidth: 280, valueFormatter: numberFormatter, cellStyle: baseCellStyle },
    { headerName: "Days of Returns", field: "totalReturnDays", filter: 'agTextColumnFilter', minWidth: 280, valueFormatter: numberFormatter, cellStyle: baseCellStyle },
    { headerName: "ROI", field: "roi", minWidth: 280, filter: 'agTextColumnFilter', cellStyle: baseCellStyle },
    { headerName: "Income / Piece", field: "incomePerPiece", minWidth: 280, filter: 'agTextColumnFilter', valueFormatter: perPieceFormatter, cellStyle: baseCellStyle },
    { headerName: "Cost / Piece", field: "costPerPiece", minWidth: 250, filter: 'agTextColumnFilter', valueFormatter: perPieceFormatter, cellStyle: baseCellStyle },
];

// ---------------------------------------------------------------------------
// ✅ RBAC — fields hidden from Client/Consultant per business requirement.
// Every other field in each report stays visible for these two roles.
// ---------------------------------------------------------------------------
const JOB_DETAIL_HIDDEN_FIELDS = [
    'cost', 'netIncome', 'responseRate', 'averageGift',
    'incomePerPiece', 'costPerPiece', 'netPerPiece', 'incomeToOutlayRatio'
];

const JOB_DAILY_HIDDEN_FIELDS = [
    'totalCost', 'productionCosts', 'roi', 'incomePerPiece', 'costPerPiece'
];

// ---------------------------------------------------------------------------
// FIELD FORMAT GROUPS (shared by Excel + PDF exports so they stay consistent)
// ---------------------------------------------------------------------------
const DATE_FIELDS = ['mailDate', 'jobDate', 'returnsThrough', 'lastUpdated', 'lastDropDate', 'date', 'firstReturnDate'];
const NUMBER_FIELDS = [
    'orderQuantity', 'quantityMailed', 'totalJobMailedVolume', 'totalJobReturns',
    'nonDonors', 'donors', 'nonDonorsDay', 'donorsDay',
    'nonDonorsCumulative', 'donorsCumulative',
    'totalNonDonors', 'totalDonors', 'daysOut', 'totalReturnDays'
];
const CURRENCY_FIELDS = [
    'income', 'cost', 'netIncome', 'averageGift', 'totalIncome',
    'totalCost', 'productionCosts', 'amountDay', 'amountCumulative'
];
const PER_PIECE_FIELDS = ['incomePerPiece', 'costPerPiece', 'netPerPiece'];

// Centralized cell formatter — one place so Excel and PDF render identically.
const formatValueForField = (field, value) => {
    if (value === null || value === undefined || value === '') return '';
    if (DATE_FIELDS.includes(field)) return dateFormatter({ value });
    if (NUMBER_FIELDS.includes(field)) return numberFormatter({ value });
    if (CURRENCY_FIELDS.includes(field)) return currencyFormatter({ value });
    if (PER_PIECE_FIELDS.includes(field)) return perPieceFormatter({ value });
    if (field === 'responseRate') return value != null ? `${value}%` : '';
    return String(value);
};

// Which fields are numeric/currency → right-aligned in PDF.
const RIGHT_ALIGN_FIELDS = [
    ...NUMBER_FIELDS, ...CURRENCY_FIELDS, ...PER_PIECE_FIELDS,
    'responseRate', 'incomeToOutlayRatio', 'roi'
];

// ---------------------------------------------------------------------------
// HELPER FUNCTIONS
// ---------------------------------------------------------------------------
const parseNumber = (val) => {
    if (val === null || val === undefined || val === "") return 0;
    const num = Number(String(val).replace(/[$,]/g, ""));
    return isNaN(num) ? 0 : num;
};

const calculateJobTotals = (data) => {
    let totalOrderQuantity = 0, totalQuantityMailed = 0, totalJobMailedVolume = 0,
        totalJobReturns = 0, totalNonDonors = 0, totalDonors = 0,
        totalIncome = 0, totalCost = 0, totalNetIncome = 0;

    const seenJobs = new Set();

    data.forEach((row) => {
        if (row?.isTotalsRow) return;

        totalOrderQuantity += parseNumber(row.orderQuantity);
        totalQuantityMailed += parseNumber(row.quantityMailed);
        totalNonDonors += parseNumber(row.nonDonors);
        totalDonors += parseNumber(row.donors);
        totalIncome += parseNumber(row.income);
        totalCost += parseNumber(row.cost);
        totalNetIncome += parseNumber(row.netIncome);

        if (!seenJobs.has(row.jobID)) {
            seenJobs.add(row.jobID);
            totalJobMailedVolume += parseNumber(row.totalJobMailedVolume);
            totalJobReturns += parseNumber(row.totalJobReturns);
        }
    });

    return {
        totalOrderQuantity, totalQuantityMailed, totalJobMailedVolume,
        totalJobReturns, totalNonDonors, totalDonors, totalIncome, totalCost, totalNetIncome
    };
};

const JobReportsTab = ({
    styles,
    clients,
    jobTypes,
    reportType,
    handleReportTypeChange,
    selectedClientId,
    setSelectedClientId,
    isClientDropdownOpen,
    setIsClientDropdownOpen,
    fetchJobsByClient,
    jobDetailsJobId,
    jobReportFilters,
    setJobReportFilters,
    handleRunReport,
    handleClearReport,
    rowData,
    searchText,
    setSearchText,
    onGridReady,
}) => {
    const [clientSearch, setClientSearch] = useState("");
    const filteredClients = clients.filter((c) =>
        c.clientName.toLowerCase().includes(clientSearch.toLowerCase())
    );

    const navigate = useNavigate();

    // ✅ RBAC — scoped to exactly Client (2) and Consultant (3); every other
    // role sees every column, matching how Reports.js gates trend-analysis.
    const userTypeID = getUserTypeID();
    const isClientOrConsultant = userTypeID === 2 || userTypeID === 3;

    const jobIdCellRendererWithNav = (params) => {
        const isTotal = params.data?.isTotalsRow;

        if (isTotal) {
            return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
        }

        const handleJobIdClick = (e) => {
            e.stopPropagation();
            const jobCode = params.data?.jobID || params.value;

            navigate(`/job-details/${jobCode}`, {
                state: {
                    jobId: jobCode,
                    source: 'reports',
                    reportFilters: jobReportFilters,
                    selectedClientId: selectedClientId,
                    reportType: reportType,
                    rowData: rowData,
                    fromReports: true
                }
            });

        };

        return (
            <span
                onClick={handleJobIdClick}
                style={{
                    color: '#2563eb',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                }}
            >
                {params.value}
            </span>
        );
    };

    // ✅ RBAC: strip the fields listed in JOB_DETAIL_HIDDEN_FIELDS /
    // JOB_DAILY_HIDDEN_FIELDS for Client and Consultant. Export (Excel/PDF)
    // reads from this same filtered list via getExportColumns(), so hidden
    // columns can never leak through an export either.
    const getActiveColumnDefs = () => {
        const baseDefs = reportType === "Job Daily" ? jobDailyColumnDefs : jobDetailColumnDefs;
        const hiddenFields = reportType === "Job Daily" ? JOB_DAILY_HIDDEN_FIELDS : JOB_DETAIL_HIDDEN_FIELDS;

        const visibleDefs = isClientOrConsultant
            ? baseDefs.filter((col) => !hiddenFields.includes(col.field))
            : baseDefs;

        return visibleDefs.map(col =>
            col.field === 'jobID'
                ? { ...col, cellRenderer: jobIdCellRendererWithNav }
                : col
        );
    };

    const activeColumnDefs = getActiveColumnDefs();

    const [jobSearch, setJobSearch] = useState("");
    const [showFilters, setShowFilters] = useState(true);
    const [showMenuDropdown, setShowMenuDropdown] = useState(false);
    const menuDropdownRef = useRef(null);

    // ✅ ref to access AgGrid's exposed methods (getVisibleColumns, etc.)
    //    Mirrors the JobsModule pattern so drag/drop column visibility is respected on export.
    const agGridRef = useRef(null);

    // ✅ grid api ref — set in onGridReady (wrapped so the parent's own
    //    onGridReady callback still fires). Used to read selected/filtered
    //    rows for export, exactly like JobsModule's gridApiRef.
    const gridApiRef = useRef(null);

    const toggleFilters = () => setShowFilters(!showFilters);

    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('success');

    const filteredJobs = (jobDetailsJobId || []).filter((j) =>
        String(j.jobCode ?? "").toLowerCase().includes(jobSearch.toLowerCase())
    );

    const clientDropdownRef = useRef();
    const jobDropdownRef = useRef();
    const [isJobDropdownOpen, setIsJobDropdownOpen] = useState(false);

    // ✅ post-filter rows — mirrors CostSummary/ListSummaryTab/PackageCostsDetails/
    //    PostageDetails/BackendCostsDetails/ListsDetails convention: null = no
    //    filter active (use all data), [] = filter matched nothing.
    const [filteredData, setFilteredData] = useState(null);
    const [totals, setTotals] = useState({
        totalOrderQuantity: 0,
        totalQuantityMailed: 0,
        totalJobMailedVolume: 0,
        totalJobReturns: 0,
        totalNonDonors: 0,
        totalDonors: 0,
        totalIncome: 0,
        totalCost: 0,
        totalNetIncome: 0,
    });

    const formatWithTotalBold = (params, formatterFn) => {
        const isTotal = params.node.rowPinned === 'bottom';
        let value = params.value ?? '';

        if (formatterFn) {
            value = formatterFn(params);
        }

        if (params.colDef?.field === 'roi') {
            const num = Number(params.value);
            value = isNaN(num) ? '0.00' : num.toFixed(2);
        }

        return isTotal
            ? `<span style="font-weight:bold;">${value}</span>`
            : value;
    };

    const defaultColDef = {
        valueFormatter: (params) => formatWithTotalBold(params)
    };

    useEffect(() => {
        setJobReportFilters((prev) => ({
            ...prev,
            jobIDs: []
        }));
    }, [selectedClientId]);

    // ✅ Drop any stale filter snapshot whenever the report data changes
    // (new report run, report type switch) — mirrors the other cost tabs.
    useEffect(() => {
        setFilteredData(null);
    }, [rowData]);

    // ------------------------------------------------------------------
    // ✅ Helper: get the list of column field/headerName pairs that should
    // appear in an export. Uses AgGrid's getVisibleColumns() so any column
    // the user dragged off the grid is automatically excluded.
    // ------------------------------------------------------------------
    const getExportColumns = () => {
        const fallback = activeColumnDefs
            .filter((col) => col.field && col.field !== 'action')
            .map((col) => ({ field: col.field, headerName: col.headerName || col.field }));

        if (agGridRef.current && typeof agGridRef.current.getVisibleColumns === 'function') {
            const visible = agGridRef.current.getVisibleColumns();
            if (Array.isArray(visible)) {
                return visible.filter((c) => c.field && c.field !== 'action');
            }
        }
        return fallback;
    };

    const getExportableRowsFrom = (source) => (source || []).filter((r) => !r?.isTotalsRow);

    // ------------------------------------------------------------------
    // ✅ getExportRows — mirrors JobsModule's full three-tier scope rule:
    // checked/"selected" rows take priority over the active grid filter,
    // which takes priority over "everything".
    //
    //   • Compute the rows AG Grid is showing after any filter/quick-search
    //     (forEachNodeAfterFilter) — the "visible" set.
    //   • Of the checked rows (getSelectedRows), keep only those still
    //     visible -> "checkedVisible".
    //   1. If checkedVisible is non-empty -> export those.
    //   2. Else if a filter is active (visible !== all) -> export all
    //      visible, even if that's zero.
    //   3. Else -> export everything.
    //
    // Returns { rows, scope } where scope is 'selected' | 'filtered' | 'all'.
    // ------------------------------------------------------------------
    const getExportRows = () => {
        const api = gridApiRef.current;
        const allRows = getExportableRowsFrom(rowData);

        let visible = null;
        if (api && typeof api.forEachNodeAfterFilter === 'function') {
            const rows = [];
            api.forEachNodeAfterFilter((node) => {
                if (node.data && !node.data.isTotalsRow) rows.push(node.data);
            });
            visible = rows;
        }

        const isFiltered = visible !== null && visible.length !== allRows.length;

        let selected = [];
        if (api && typeof api.getSelectedRows === 'function') {
            selected = api.getSelectedRows().filter((r) => r && !r.isTotalsRow);
        }

        if (selected.length > 0) {
            if (visible !== null) {
                const visibleSet = new Set(visible);
                const checkedVisible = selected.filter((r) => visibleSet.has(r));
                if (checkedVisible.length > 0) {
                    return { rows: checkedVisible, scope: 'selected' };
                }
            } else {
                return { rows: selected, scope: 'selected' };
            }
        }

        if (isFiltered) {
            return { rows: visible, scope: 'filtered' };
        }

        if (allRows.length === 0) {
            return { rows: [], scope: 'all' };
        }
        return {
            rows: visible !== null ? visible : allRows,
            scope: 'all',
        };
    };

    // ✅ EXPORT TO EXCEL FUNCTION (respects column visibility, selection, and filter scope)
    const exportToExcel = () => {
        const { rows: exportRows } = getExportRows();

        if (!exportRows || exportRows.length === 0) {
            setMessage('No data to export');
            setMessageType('error');
            setShowMenuDropdown(false);
            return;
        }

        try {
            const columnsToExport = getExportColumns();
            if (columnsToExport.length === 0) {
                setMessage('No data to export');
                setMessageType('error');
                setShowMenuDropdown(false);
                return;
            }

            const exportData = exportRows.map((row) => {
                const formattedRow = {};
                columnsToExport.forEach(({ field, headerName }) => {
                    formattedRow[headerName] = formatValueForField(field, row[field]);
                });
                return formattedRow;
            });

            if (reportType === 'Job Detail') {
                const totalsRowExport = {};
                columnsToExport.forEach(({ field, headerName }, idx) => {
                    if (idx === 0) {
                        totalsRowExport[headerName] = '';
                        return;
                    }
                    const value = totalsRow[field];
                    totalsRowExport[headerName] =
                        (value !== null && value !== undefined && value !== '')
                            ? formatValueForField(field, value)
                            : '';
                });
                exportData.push(totalsRowExport);
            }

            const ws = XLSX.utils.json_to_sheet(exportData);

            const colWidths = [];
            Object.keys(exportData[0] || {}).forEach((key, index) => {
                const maxLength = Math.max(
                    key.length,
                    ...exportData.map((row) => {
                        const value = row[key];
                        return value ? String(value).length : 0;
                    })
                );
                colWidths[index] = { wch: Math.min(maxLength + 2, 50) };
            });
            ws['!cols'] = colWidths;

            const wb = XLSX.utils.book_new();
            const sheetName = reportType === 'Job Daily' ? 'Job Daily' : 'Job Details';
            XLSX.utils.book_append_sheet(wb, ws, sheetName);

            const currentDate = new Date();
            const dateStr = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1)
                .toString()
                .padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
            const fileName = `${sheetName}_${dateStr}.xlsx`;

            XLSX.writeFile(wb, fileName);
            setMessage('Export completed successfully!');
            setMessageType('success');
            setShowMenuDropdown(false);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            setMessage('Error exporting data to Excel');
            setMessageType('error');
            setShowMenuDropdown(false);
        }
    };

    // ✅ PRINT TO PDF FUNCTION (styled header card; KPI bar + TOTAL only for Job Detail; respects selection/filter scope)
    const printToPDF = () => {
        const { rows: validData, scope } = getExportRows();

        if (!validData || validData.length === 0) {
            setMessage('No data to export');
            setMessageType('error');
            setShowMenuDropdown(false);
            return;
        }

        try {
            const columnsToExport = getExportColumns();
            if (columnsToExport.length === 0) {
                setMessage('No data to export');
                setMessageType('error');
                setShowMenuDropdown(false);
                return;
            }

            const isJobDetail = reportType === 'Job Detail';

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'pt',
                format: 'a4',
            });

            const pageWidth = doc.internal.pageSize.getWidth();
            const marginX = 20;
            const cardX = marginX;
            const cardY = 20;
            const cardWidth = pageWidth - marginX * 2;

            const titleText = isJobDetail ? 'Job Detail Report'.toUpperCase() : 'Job Daily Report'.toUpperCase();

            const currentDate = new Date();
            const reportDateStr = `${(currentDate.getMonth() + 1)
                .toString()
                .padStart(2, '0')}/${currentDate.getDate().toString().padStart(2, '0')}/${(currentDate.getFullYear() % 100).toString().padStart(2, '0')}`;

            const headerBarHeight = 60;
            const statsBarHeight = isJobDetail ? 50 : 0;
            const detailsBarHeight = 22;
            const cardHeight = headerBarHeight + statsBarHeight + detailsBarHeight;

            const BLUE_DARK = [30, 75, 120];
            const BLUE_MID = [46, 111, 159];
            const BLUE_DEEP = [22, 58, 95];

            doc.setFillColor(...BLUE_DARK);
            doc.rect(cardX, cardY, cardWidth, headerBarHeight, 'F');

            const logoW = 90;
            doc.setFillColor(...BLUE_DEEP);
            doc.rect(cardX, cardY, logoW, headerBarHeight, 'F');

            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            doc.text('Rockwell', cardX + logoW / 2, cardY + headerBarHeight / 2 - 2, { align: 'center' });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6);
            doc.setTextColor(180, 200, 220);
            doc.text('— SOLUTIONS —', cardX + logoW / 2, cardY + headerBarHeight / 2 + 9, { align: 'center' });

            const titleAreaX = cardX + logoW;
            const titleAreaW = cardWidth - logoW;
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(18);
            doc.text(titleText, titleAreaX + titleAreaW / 2, cardY + 28, { align: 'center' });

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(200, 215, 230);
            doc.text('', titleAreaX + titleAreaW / 2, cardY + 44, { align: 'center' });

            const metaX = cardX + cardWidth - 12;
            doc.setFontSize(7);
            doc.setTextColor(220, 230, 240);
            doc.setFont('helvetica', 'normal');
            doc.text(`Report: ${reportDateStr}`, metaX, cardY + 16, { align: 'right' });

            if (isJobDetail) {
                const statsY = cardY + headerBarHeight;
                doc.setFillColor(...BLUE_MID);
                doc.rect(cardX, statsY, cardWidth, statsBarHeight, 'F');

                // ✅ RBAC: strip cost-side KPI cells for Client/Consultant —
                // mirrors JOB_DETAIL_HIDDEN_FIELDS (cost, netIncome hidden).
                const stats = [
                    { label: 'QTY MAILED', value: numberFormatter({ value: totals.totalQuantityMailed }) || '0' },
                    { label: 'DONORS', value: numberFormatter({ value: totals.totalDonors }) || '0' },
                    { label: 'INCOME', value: currencyFormatter({ value: totals.totalIncome }) || '$0' },
                    ...(!isClientOrConsultant ? [
                        { label: 'TOTAL COST', value: currencyFormatter({ value: totals.totalCost }) || '$0' },
                        { label: 'NET INCOME', value: currencyFormatter({ value: totals.totalNetIncome }) || '$0' },
                    ] : []),
                ];

                const statCellW = cardWidth / stats.length;
                stats.forEach((stat, i) => {
                    const cellX = cardX + i * statCellW;

                    if (i > 0) {
                        doc.setDrawColor(80, 130, 170);
                        doc.setLineWidth(0.5);
                        doc.line(cellX, statsY + 6, cellX, statsY + statsBarHeight - 6);
                    }

                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(6.5);
                    doc.setTextColor(200, 220, 235);
                    doc.text(stat.label, cellX + statCellW / 2, statsY + 15, { align: 'center' });

                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(11);
                    doc.setTextColor(255, 255, 255);
                    doc.text(stat.value, cellX + statCellW / 2, statsY + 35, { align: 'center' });
                });
            }

            const detailsY = cardY + headerBarHeight + statsBarHeight;
            doc.setFillColor(...BLUE_DEEP);
            doc.rect(cardX, detailsY, cardWidth, detailsBarHeight, 'F');

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(255, 255, 255);
            doc.text(isJobDetail ? 'JOB DETAILS' : 'DAILY DETAILS', cardX + 14, detailsY + detailsBarHeight / 2 + 3);

            const scopeLabel =
                scope === 'selected' ? 'Selected'
                    : scope === 'filtered' ? 'Filtered'
                        : 'All';
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
            doc.setTextColor(200, 215, 230);
            doc.text(
                `${scopeLabel} Records: ${validData.length}  ·  Confidential`,
                cardX + cardWidth - 12,
                detailsY + detailsBarHeight / 2 + 3,
                { align: 'right' }
            );

            const tableStartY = cardY + cardHeight + 10;

            const head = [columnsToExport.map((c) => c.headerName)];

            const body = validData.map((row) =>
                columnsToExport.map(({ field }) => formatValueForField(field, row[field]))
            );

            const tableConfig = {
                head,
                body,
                startY: tableStartY,
                margin: { left: marginX, right: marginX },
                styles: {
                    fontSize: 5,
                    cellPadding: 3,
                    overflow: 'linebreak',
                    valign: 'middle',
                },
                headStyles: {
                    fillColor: [46, 111, 159],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: 5,
                },
                alternateRowStyles: {
                    fillColor: [250, 250, 250],
                },
                didParseCell: (data) => {
                    if (data.section === 'body' || data.section === 'foot') {
                        const col = columnsToExport[data.column.index];
                        if (col && RIGHT_ALIGN_FIELDS.includes(col.field)) {
                            data.cell.styles.halign = 'right';
                        }
                    }
                },
                didDrawPage: () => {
                    const pageCount = doc.internal.getNumberOfPages();
                    const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
                    const pageHeight = doc.internal.pageSize.getHeight();
                    doc.setFontSize(8);
                    doc.setTextColor(120, 120, 120);
                    doc.text(
                        `Page ${pageCurrent} of ${pageCount}`,
                        pageWidth - marginX,
                        pageHeight - 10,
                        { align: 'right' }
                    );
                },
            };

            if (isJobDetail) {
                const footerRow = columnsToExport.map(({ field }, idx) => {
                    if (idx === 0) return 'TOTAL';
                    const value = totalsRow[field];
                    return (value !== null && value !== undefined && value !== '')
                        ? formatValueForField(field, value)
                        : '';
                });
                tableConfig.foot = [footerRow];
                tableConfig.footStyles = {
                    fillColor: [240, 248, 255],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                };
            }

            autoTable(doc, tableConfig);

            const fileLabel = isJobDetail ? 'Job-Details' : 'Job-Daily';
            doc.save(`${fileLabel}-${new Date().toISOString().split('T')[0]}.pdf`);

            setMessage('Export completed successfully!');
            setMessageType('success');
            setShowMenuDropdown(false);
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            setMessage('Error exporting data to PDF');
            setMessageType('error');
            setShowMenuDropdown(false);
        }
    };

    const handleMessageClose = () => {
        setMessage('');
        setMessageType('success');
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuDropdownRef.current && !menuDropdownRef.current.contains(event.target)) {
                setShowMenuDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
                setIsClientDropdownOpen(false);
            }
            if (jobDropdownRef.current && !jobDropdownRef.current.contains(e.target)) {
                setIsJobDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // ------------------------------------------------------------------
    // ✅ handleFilterChanged — falls back to gridApiRef.current, matching
    // JobsModule, and distinguishes "no filter active" (null) from
    // "filter matched nothing" ([]).
    // ------------------------------------------------------------------
    const handleFilterChanged = (params) => {
        const api = params?.api || gridApiRef.current;
        if (!api || typeof api.forEachNodeAfterFilter !== 'function') return;

        const allCount = (rowData || []).filter((r) => !r?.isTotalsRow).length;
        const filteredRows = [];
        api.forEachNodeAfterFilter((node) => {
            if (node.data && !node.data.isTotalsRow) {
                filteredRows.push(node.data);
            }
        });

        if (filteredRows.length < allCount) {
            setFilteredData(filteredRows);
        } else {
            setFilteredData(null);
        }
    };

    useEffect(() => {
        const sourceData = filteredData !== null ? filteredData : (rowData || []);
        setTotals(calculateJobTotals(sourceData));
    }, [rowData, filteredData]);

    const totalsRow = {
        isTotalsRow: true,
        clientID: "",
        clientName: "TOTAL",
        jobID: "",
        jobName: "",
        jobTypeCode: "",
        jobType: "",
        mailCode: "",
        test: "",
        postage: "",
        listName: "",
        listSelect: "",
        mailDate: "",
        jobDate: "",
        returnsThrough: "",
        lastUpdated: "",
        lastUpdatedBy: "",
        orderQuantity: totals.totalOrderQuantity,
        quantityMailed: totals.totalQuantityMailed,
        totalJobMailedVolume: totals.totalJobMailedVolume,
        totalJobReturns: totals.totalJobReturns,
        nonDonors: totals.totalNonDonors,
        donors: totals.totalDonors,
        income: totals.totalIncome,
        cost: totals.totalCost,
        netIncome: totals.totalNetIncome,
        responseRate:
            totals.totalQuantityMailed > 0
                ? ((totals.totalDonors / totals.totalQuantityMailed) * 100).toFixed(2)
                : 0,
        averageGift:
            totals.totalDonors > 0
                ? (totals.totalIncome / totals.totalDonors).toFixed(2)
                : 0,
        incomePerPiece:
            totals.totalQuantityMailed > 0
                ? (totals.totalIncome / totals.totalQuantityMailed).toFixed(2)
                : 0,
        costPerPiece:
            totals.totalQuantityMailed > 0
                ? (totals.totalCost / totals.totalQuantityMailed).toFixed(2)
                : 0,
        netPerPiece:
            totals.totalQuantityMailed > 0
                ? (totals.totalNetIncome / totals.totalQuantityMailed).toFixed(2)
                : 0,
        incomeToOutlayRatio:
            totals.totalCost > 0
                ? (totals.totalIncome / totals.totalCost).toFixed(2)
                : 0,
        firstReturnDate: "",
    };

    const toggleMenuDropdown = () => setShowMenuDropdown(!showMenuDropdown);

    return (
        <>

            <SuccessMessage
                message={message}
                onClose={handleMessageClose}
                type={messageType}
            />

            {/* Type of Report */}
            <div style={{ marginTop: "20px", width: "250px" }}>
                <label>Type of Report</label>
                <select style={styles.input} value={reportType} onChange={handleReportTypeChange}>
                    <option>Job Detail</option>
                    <option>Job Daily</option>
                </select>
            </div>

            {/* Filters */}
            <div className="report-filter-container">
                <div className="filter-header" style={{
                    display: 'flex', justifyContent: 'space-between',
                    padding: '0px 10px 0px 0px', alignItems: 'center',
                    backgroundColor: '#2E6F9F', borderRadius: '8px 8px 0px 0px'
                }}>
                    <h2 className='filter-heading' style={{ margin: 0, color: '#fff' }}>Filters</h2>
                    <button
                        onClick={toggleFilters}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            fontSize: '12px', color: '#fff', padding: '2px 6px',
                            transition: 'transform 0.2s ease',
                            transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                        aria-label={showFilters ? 'Hide filters' : 'Show filters'}
                    >
                        <img src="../assets/upward-arrow-icon.png"
                            alt={showFilters ? 'Hide filters' : 'Show filters'}
                            style={{ width: '12px', height: '12px', transition: 'transform 0.2s ease' }} />
                    </button>
                </div>

                {showFilters && (
                    <>
                        <div className="report-filter-body">
                            {/* ROW 1 */}
                            <div className="report-filter-row">
                                <div className="report-filter-field">
                                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Client(s)</label>
                                    <div ref={clientDropdownRef} style={{ position: "relative", width: "100%" }}>
                                        <div
                                            onClick={() => setIsClientDropdownOpen(true)}
                                            style={{
                                                minHeight: "38px",
                                                maxHeight: "100px",
                                                border: "1px solid #ccc",
                                                borderRadius: "6px",
                                                padding: "0px 8px",
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: "4px",
                                                backgroundColor: "#fff",
                                                alignItems: "center",
                                                alignContent: "flex-start",
                                                cursor: "text",
                                                overflowY: "auto"
                                            }}
                                        >
                                            {selectedClientId.length > 1 ? (
                                                <span style={{ background: "#e9ecef", padding: "2px 6px", borderRadius: "3px", whiteSpace: "nowrap" }}>
                                                    {selectedClientId.length} clients selected
                                                    <span
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedClientId([]);
                                                            fetchJobsByClient([]);
                                                            setJobReportFilters((prev) => ({
                                                                ...prev,
                                                                jobIDs: []
                                                            }));

                                                        }}
                                                        style={{ marginLeft: 6, cursor: "pointer" }}
                                                    >
                                                        ×
                                                    </span>
                                                </span>
                                            ) : (
                                                clients
                                                    .filter((c) => selectedClientId.includes(c.clientID))
                                                    .map((c) => (
                                                        <span
                                                            key={c.clientID}
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                background: "#e9ecef",
                                                                padding: "2px 6px",
                                                                borderRadius: "3px",
                                                                fontSize: "13px",
                                                                whiteSpace: "nowrap"
                                                            }}
                                                        >
                                                            {c.clientName}
                                                            <span
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const updated = selectedClientId.filter((id) => id !== c.clientID);
                                                                    setSelectedClientId(updated);
                                                                    fetchJobsByClient(updated);
                                                                    setJobReportFilters((prev) => ({
                                                                        ...prev,
                                                                        jobIDs: []
                                                                    }));

                                                                }}
                                                                style={{ marginLeft: 6, cursor: "pointer" }}
                                                            >
                                                                ×
                                                            </span>
                                                        </span>
                                                    ))
                                            )}

                                            <input
                                                type="text"
                                                placeholder={selectedClientId.length === 0 ? "Select clients..." : ""}
                                                value={clientSearch}
                                                onChange={(e) => {
                                                    setClientSearch(e.target.value);
                                                    setIsClientDropdownOpen(true);
                                                }}
                                                style={{
                                                    flex: 1,
                                                    minWidth: selectedClientId.length === 0 ? "120px" : "40px",
                                                    width: "40px",
                                                    border: "none",
                                                    outline: "none",
                                                    fontSize: "14px",
                                                    padding: 0
                                                }}
                                            />
                                        </div>

                                        {isClientDropdownOpen && (
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    top: "100%",
                                                    left: 0,
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                    border: "1px solid #ccc",
                                                    borderRadius: "4px",
                                                    marginTop: "4px",
                                                    zIndex: 9999,
                                                    maxHeight: "250px",
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                                    overflow: "hidden"
                                                }}
                                            >
                                                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px", borderBottom: "1px solid #eee" }}>
                                                    <button style={{
                                                        background: 'none', border: '1px solid #2E6F9F', color: '#2E6F9F',
                                                        padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                                                    }} onClick={() => {
                                                        const allIds = filteredClients.map((c) => c.clientID);
                                                        const merged = [...new Set([...selectedClientId, ...allIds])];
                                                        setSelectedClientId(merged);
                                                        fetchJobsByClient(merged);
                                                    }}>Select All</button>
                                                    <button style={{
                                                        background: 'none', border: '1px solid #dc3545', color: '#dc3545',
                                                        padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                                                    }} onClick={() => {
                                                        setSelectedClientId([]);
                                                        fetchJobsByClient([]);
                                                        setJobReportFilters((prev) => ({
                                                            ...prev,
                                                            jobIDs: []
                                                        }));
                                                    }}>Clear All</button>
                                                </div>

                                                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                                                    {filteredClients.length > 0 ? (
                                                        filteredClients.map((client) => (
                                                            <div
                                                                key={client.clientID}
                                                                onClick={() => {
                                                                    const updated = selectedClientId.includes(client.clientID)
                                                                        ? selectedClientId.filter((id) => id !== client.clientID)
                                                                        : [...selectedClientId, client.clientID];

                                                                    setSelectedClientId(updated);
                                                                    fetchJobsByClient(updated);

                                                                    setJobReportFilters((prev) => ({
                                                                        ...prev,
                                                                        jobIDs: []
                                                                    }));

                                                                    setClientSearch("");
                                                                }}
                                                                style={{
                                                                    padding: "8px",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    cursor: "pointer",
                                                                    backgroundColor: selectedClientId.includes(client.clientID) ? "#e6f7ff" : "#fff"
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedClientId.includes(client.clientID)}
                                                                    readOnly
                                                                    style={{ marginRight: "6px" }}
                                                                />
                                                                <span style={{ fontSize: "12px" }}>{client.clientName}</span>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div style={{ padding: "8px", color: "#888" }}>No clients found</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="report-filter-field">
                                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Job ID</label>
                                    <div ref={jobDropdownRef} style={{ position: "relative", width: "100%" }}>
                                        <div
                                            onClick={() => setIsJobDropdownOpen(true)}
                                            style={{
                                                minHeight: "38px",
                                                maxHeight: "100px",
                                                border: "1px solid #ccc",
                                                borderRadius: "6px",
                                                padding: "0px 8px",
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: "4px",
                                                backgroundColor: "#fff",
                                                alignItems: "center",
                                                alignContent: "flex-start",
                                                cursor: "text",
                                                overflowY: "auto"
                                            }}
                                        >
                                            {jobReportFilters?.jobIDs?.length > 1 ? (
                                                <span style={{ background: "#e9ecef", padding: "2px 6px", borderRadius: "3px", whiteSpace: "nowrap" }}>
                                                    {jobReportFilters?.jobIDs?.length} jobs selected
                                                    <span
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setJobReportFilters((p) => ({ ...p, jobIDs: [] }));
                                                        }}
                                                        style={{ marginLeft: 6, cursor: "pointer" }}
                                                    >
                                                        ×
                                                    </span>
                                                </span>
                                            ) : (
                                                (jobDetailsJobId || [])
                                                    .filter((j) => jobReportFilters?.jobIDs.includes(j.jobID))
                                                    .map((j) => (
                                                        <span
                                                            key={j.jobID}
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                background: "#e9ecef",
                                                                padding: "2px 6px",
                                                                borderRadius: "3px",
                                                                fontSize: "13px",
                                                                whiteSpace: "nowrap"
                                                            }}
                                                        >
                                                            {j.jobCode}
                                                            <span
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setJobReportFilters((p) => ({
                                                                        ...p,
                                                                        jobIDs: p.jobIDs.filter((id) => id !== j.jobID)
                                                                    }));
                                                                }}
                                                                style={{ marginLeft: 6, cursor: "pointer" }}
                                                            >
                                                                ×
                                                            </span>
                                                        </span>
                                                    ))
                                            )}

                                            <input
                                                type="text"
                                                placeholder={jobReportFilters?.jobIDs?.length === 0 ? "Select jobs..." : ""}
                                                value={jobSearch}
                                                onChange={(e) => {
                                                    setJobSearch(e.target.value);
                                                    setIsJobDropdownOpen(true);
                                                }}
                                                style={{
                                                    flex: 1,
                                                    minWidth: jobReportFilters?.jobIDs?.length === 0 ? "120px" : "40px",
                                                    width: "40px",
                                                    border: "none",
                                                    outline: "none",
                                                    fontSize: "14px",
                                                    padding: 0
                                                }}
                                            />
                                        </div>

                                        {isJobDropdownOpen && (
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    top: "100%",
                                                    left: 0,
                                                    width: "100%",
                                                    backgroundColor: "#fff",
                                                    border: "1px solid #ccc",
                                                    borderRadius: "4px",
                                                    marginTop: "4px",
                                                    zIndex: 9999,
                                                    maxHeight: "250px",
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                                    overflow: "hidden"
                                                }}
                                            >
                                                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px", borderBottom: "1px solid #eee" }}>
                                                    <button style={{
                                                        background: 'none', border: '1px solid #2E6F9F', color: '#2E6F9F',
                                                        padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                                                    }} onClick={() => {
                                                        const allIds = filteredJobs.map((j) => j.jobID);
                                                        const merged = [...new Set([...jobReportFilters.jobIDs, ...allIds])];
                                                        setJobReportFilters((p) => ({ ...p, jobIDs: merged }));
                                                    }}>Select All</button>
                                                    <button style={{
                                                        background: 'none', border: '1px solid #dc3545', color: '#dc3545',
                                                        padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                                                    }} onClick={() => {
                                                        setJobReportFilters((p) => ({ ...p, jobIDs: [] }));
                                                    }}>Clear All</button>
                                                </div>

                                                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                                                    {filteredJobs.length > 0 ? (
                                                        filteredJobs.map((job) => (
                                                            <div
                                                                key={job.jobID}
                                                                onClick={() => {
                                                                    const updated = jobReportFilters.jobIDs.includes(job.jobID)
                                                                        ? jobReportFilters.jobIDs.filter((id) => id !== job.jobID)
                                                                        : [...jobReportFilters.jobIDs, job.jobID];
                                                                    setJobReportFilters((p) => ({ ...p, jobIDs: updated }));
                                                                    setJobSearch("");
                                                                }}
                                                                style={{
                                                                    padding: "8px",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    cursor: "pointer",
                                                                    backgroundColor: jobReportFilters.jobIDs.includes(job.jobID) ? "#e6f7ff" : "#fff"
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={jobReportFilters.jobIDs.includes(job.jobID)}
                                                                    readOnly
                                                                    style={{ marginRight: "6px" }}
                                                                />
                                                                <span style={{ fontSize: "12px" }}>{job.jobCode}</span>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div style={{ padding: "8px", color: "#888" }}>No jobs found</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mail-date-filter">
                                    <label className="mail-date-filter-label">
                                        Mail Date
                                    </label>
                                    <div className="">
                                        <div className="date-input-wrapper">
                                            <input
                                                type="date"
                                                className={`mail-date-input ${!jobReportFilters.mailStartDate ? 'empty' : ''}`}
                                                value={jobReportFilters.mailStartDate}
                                                onChange={(e) => {
                                                    const newFromDate = e.target.value;
                                                    if (jobReportFilters.mailEndDate && newFromDate > jobReportFilters.mailEndDate) {
                                                        return;
                                                    }
                                                    setJobReportFilters((p) => ({ ...p, mailStartDate: newFromDate }));
                                                }}
                                                max={jobReportFilters.mailEndDate || undefined}
                                                title="Start Date"
                                            />
                                        </div>
                                        <span className="arrow-icon">→</span>
                                        <div className="date-input-wrapper">
                                            <input
                                                type="date"
                                                className={`mail-date-input ${!jobReportFilters.mailEndDate ? 'empty' : ''}`}
                                                value={jobReportFilters.mailEndDate}
                                                onChange={(e) => {
                                                    const newToDate = e.target.value;
                                                    if (jobReportFilters.mailStartDate && newToDate < jobReportFilters.mailStartDate) {
                                                        return;
                                                    }
                                                    setJobReportFilters((p) => ({ ...p, mailEndDate: newToDate }));
                                                }}
                                                min={jobReportFilters.mailStartDate || undefined}
                                                title="End Date"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="report-filter-field">
                                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Job Type</label>
                                    <select
                                        value={jobReportFilters.jobTypeIDs}
                                        onChange={(e) =>
                                            setJobReportFilters((p) => ({ ...p, jobTypeIDs: e.target.value }))
                                        }
                                    >
                                        <option value="">Select Job Type</option>
                                        {jobTypes?.map((jobType) => (
                                            <option key={jobType.jobTypeID} value={jobType.jobTypeID}>
                                                {jobType.jobTypeName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="report-filter-field">
                                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Mail Code</label>
                                    <input
                                        type="text"
                                        placeholder="Search..."
                                        value={jobReportFilters.mailDetailIDs}
                                        onChange={(e) =>
                                            setJobReportFilters((p) => ({ ...p, mailDetailIDs: e.target.value }))
                                        }
                                    />
                                </div>
                                {reportType === 'Job Detail' &&
                                    <div className="report-filter-field">
                                        <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>List Name</label>
                                        <input
                                            type="text"
                                            placeholder="Search..."
                                            value={jobReportFilters.listMasterIDs}
                                            onChange={(e) =>
                                                setJobReportFilters((p) => ({ ...p, listMasterIDs: e.target.value }))
                                            }
                                        />
                                    </div>}
                                {reportType === 'Job Daily' &&
                                    <div className="mail-date-filter">
                                        <label className="mail-date-filter-label">
                                            Tallies Date
                                        </label>
                                        <div className="">
                                            <div className="date-input-wrapper">
                                                <input
                                                    type="date"
                                                    className={`mail-date-input ${!jobReportFilters.talliesStartDate ? 'empty' : ''}`}
                                                    value={jobReportFilters.talliesStartDate}
                                                    onChange={(e) => {
                                                        const newFromDate = e.target.value;
                                                        if (jobReportFilters.talliesEndDate && newFromDate > jobReportFilters.talliesEndDate) {
                                                            return;
                                                        }
                                                        setJobReportFilters((p) => ({ ...p, talliesStartDate: newFromDate }));
                                                    }}
                                                    max={jobReportFilters.talliesEndDate || undefined}
                                                    title="Start Date"
                                                />
                                            </div>
                                            <span className="arrow-icon">→</span>
                                            <div className="date-input-wrapper">
                                                <input
                                                    type="date"
                                                    className={`mail-date-input ${!jobReportFilters.talliesEndDate ? 'empty' : ''}`}
                                                    value={jobReportFilters.talliesEndDate}
                                                    onChange={(e) => {
                                                        const newToDate = e.target.value;
                                                        if (jobReportFilters.talliesStartDate && newToDate < jobReportFilters.talliesStartDate) {
                                                            return;
                                                        }
                                                        setJobReportFilters((p) => ({ ...p, talliesEndDate: newToDate }));
                                                    }}
                                                    min={jobReportFilters.talliesStartDate || undefined}
                                                    title="End Date"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                }
                            </div>

                            {/* ROW 2 */}
                            <div className="report-filter-row" style={{ justifyContent: "flex-end" }}>
                                <div className="report-filter-buttons">
                                    {(() => {
                                        const hasFilters =
                                            selectedClientId.length > 0 ||
                                            jobReportFilters.jobIDs.length > 0 ||
                                            !!jobReportFilters.mailStartDate ||
                                            !!jobReportFilters.mailEndDate ||
                                            !!jobReportFilters.jobTypeIDs ||
                                            !!jobReportFilters.mailDetailIDs ||
                                            !!jobReportFilters.listMasterIDs ||
                                            !!jobReportFilters.talliesStartDate ||
                                            !!jobReportFilters.talliesEndDate;
                                        return (
                                            <>
                                                <button
                                                    className="report-btn-run"
                                                    onClick={handleRunReport}
                                                    disabled={!hasFilters}
                                                    style={!hasFilters ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                                                >
                                                    Run Report
                                                </button>
                                                <button
                                                    className="report-btn-clear"
                                                    onClick={handleClearReport}
                                                    disabled={!hasFilters}
                                                    style={!hasFilters ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                                                >
                                                    Clear
                                                </button>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* ✅ EXPORT MENU BUTTON */}
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', marginRight: '20px' }}>
                <div style={{ position: 'relative' }} ref={menuDropdownRef}>
                    <button className="job-details-menu-button" onClick={toggleMenuDropdown}>
                        <span className="menu-dot"></span>
                        <span className="menu-dot"></span>
                        <span className="menu-dot"></span>
                    </button>

                    {showMenuDropdown && (
                        <div style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            marginTop: '8px',
                            backgroundColor: '#fff',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            zIndex: 1000,
                            minWidth: '200px',
                            overflow: 'hidden'
                        }}>
                            {/* Export to Excel */}
                            <div
                                onClick={exportToExcel}
                                style={{
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'background-color 0.2s',
                                    fontSize: '14px',
                                    color: '#333',
                                    borderBottom: '1px solid #f0f0f0'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="8" y1="13" x2="16" y2="13" />
                                    <line x1="8" y1="17" x2="16" y2="17" />
                                    <polyline points="10 9 9 9 8 9" />
                                </svg>
                                Export to Excel
                            </div>

                            {/* Print To Pdf */}
                            <div
                                onClick={printToPDF}
                                style={{
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'background-color 0.2s',
                                    fontSize: '14px',
                                    color: '#333'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="6 9 6 2 18 2 18 9" />
                                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                                    <rect x="6" y="14" width="12" height="8" />
                                </svg>
                                Print To PDF
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Grid */}
            <div style={{ marginTop: "20px" }}>
                <AgGrid
                    key={reportType}
                    ref={agGridRef}
                    enableSorting={true}
                    enableFilter={true}
                    pagination={false}
                    height="500px"
                    quickFilterText={searchText}
                    hideExportButton={true}
                    hideSideBar={true}
                    onQuickFilterChange={setSearchText}
                    rowData={rowData}
                    columnDefs={activeColumnDefs}
                    onGridReady={(params) => {
                        gridApiRef.current = params.api;
                        if (typeof onGridReady === 'function') onGridReady(params);
                    }}
                    onFilterChanged={handleFilterChanged}
                    pinnedBottomRowData={reportType === "Job Detail" ? [totalsRow] : []}

                    frameworkComponents={{
                        customTooltip: CustomTooltip
                    }}

                    gridOptions={{
                        tooltipShowDelay: 0,
                        tooltipHideDelay: 1000,

                        suppressRowClickSelection: true,
                        rowSelection: 'multiple',
                        isRowSelectable: (params) => !params.data || !params.data.isTotalsRow,

                        overlayNoRowsTemplate: `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
      text-align: center;
    ">

      <div style="font-size: 48px; margin-bottom: 16px; opacity: 0.5;">
        📋
      </div>

      <div style="
        font-size: 16px;
        color: #6c757d;
        font-weight: 500;
      ">
        No records found
      </div>

    </div>
      `,
                        getRowStyle: (params) => {
                            if (params.node.rowPinned === "bottom") {
                                return {
                                    fontWeight: "bold",
                                    backgroundColor: "#d3eaff",
                                };
                            }
                        },
                    }}
                />
            </div>
        </>
    );
};

export default JobReportsTab;
